// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * The controls row attached to the carousel.
 *
 * Composes three optional subcomponents in a single horizontal strip:
 *
 *     ┌──────────┐  ┌──────────┐  ┌──────────┐  ┌──────────┐
 *     │ prev ◂─  │  │  • • o   │  │ ─────▭── │  │ ─▸ next  │
 *     └──────────┘  └──────────┘  └──────────┘  └──────────┘
 *      arrow         pagination     scrollbar     arrow
 *
 * Used both for the outer (below-carousel) layout and the inline (overlayed)
 * layout. The scrollbar is omitted when the carousel scrolls vertically.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ArrowNavigation from "@stoneage/App/Components/EmblaCarousel/ArrowNavigation";
import { useCarouselContext } from "@stoneage/App/Components/EmblaCarousel/CarouselContext";
import Pagination from "@stoneage/App/Components/EmblaCarousel/Pagination";
import { Scrollbar, ScrollbarMode } from "@stoneage/App/Components/EmblaCarousel/Scrollbar";
import React, { FC } from "react";

/** Props for the {@link CarouselControls} component. */
export interface CarouselControlsProps {
    /** Show the next/previous arrow buttons. */
    showNavigation: boolean;
    /** Show the pagination dots. */
    showPagination: boolean;
    /** Whether pagination dots are clickable. */
    paginationClickable: boolean;
    /** Show the scrollbar. Automatically hidden for vertical carousels. */
    showScrollbar: boolean;
    /** Whether the scrollbar thumb is draggable. */
    scrollbarDraggable: boolean;
    /** Scrollbar progress strategy. Defaults to `"stepper"`. */
    scrollbarMode?: ScrollbarMode;
    /** Render the row absolutely positioned on top of the slide (overlay style). */
    inline?: boolean;
    /** Position the arrows outside (flanking) the carousel container. */
    navigationOutside?: boolean;
    /** Allow pause autoplay */
    pausable?: boolean;
}

export const CarouselControls: FC<CarouselControlsProps> = ({
    showNavigation,
    showPagination,
    paginationClickable,
    showScrollbar,
    scrollbarDraggable,
    scrollbarMode = "stepper",
    inline = false,
    navigationOutside,
    pausable,
}) => {
    const ctx = useCarouselContext();
    const carouselApi = ctx?.carouselApi ?? null;
    const isVertical = ctx?.direction === "vertical";

    const containerClass = `xng-controls-container${inline ? " inline" : ""}`;

    return (
        <StyledWrapper className={containerClass}>
            {showNavigation && !navigationOutside && <ArrowNavigation arrowType="prev" showOnMobile />}

            {showPagination && <Pagination pausable={pausable} clickable={paginationClickable} />}

            {showScrollbar && !isVertical && (
                <Scrollbar
                    carouselApi={carouselApi}
                    isVertical={isVertical}
                    isDraggable={scrollbarDraggable}
                    mode={scrollbarMode}
                />
            )}

            {showNavigation && !navigationOutside && <ArrowNavigation arrowType="next" showOnMobile />}
        </StyledWrapper>
    );
};

export default CarouselControls;
